import {
  Box,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Toolbar,
} from "@mui/material";

import TableCellWithHover from "./TableCellWithHover";
import {
  IncidentAreaFilterOperator,
  IncidentTableData,
  IncidentTableSortKey,
  ReadOnlyRiskIncidentDataProps,
  incidentTypes,
} from "../types";
import { Row } from "./RowComponent";
import { geojsonFileMessage } from "./TooltipMessages";
import { ReactNode } from "react";
import { styleMapper } from "../map/styles";
import SectionCollapsible from "../../../../common/SectionCollapsible";
import { useIncidentTableFilters } from "./hooks/useIncidentTableFilters";
import { useIncidentTableSort } from "./hooks/useIncidentTableSort";
import { useIncidentTablePagination } from "./hooks/useIncidentTablePagination";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { LocalizationProvider } from "@mui/x-date-pickers";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { IncidentDatePicker } from "./IncidentDatePicker";
import { IncidentDateFieldSwitch } from "./IncidentDateFieldSwitch";
import { incidentTableStyles } from "./styles";

function ReadOnlyRiskIncidentData({
  solutionId,
  page,
  rowsPerPage,
  setPage,
  rows,
  selectedIncident,
  selectedIncidents,
  setSelectedIncident,
  setSelectedIncidents,
}: ReadOnlyRiskIncidentDataProps) {
  const selectedStyle = selectedIncident
    ? styleMapper[selectedIncident.type]
    : null;

  const {
    filters,
    filteredRows,
    boundaryFilterOptions,
    updateFilter,
    resetFilters,
  } = useIncidentTableFilters({ rows, setPage });

  const { sortedRows, sortState, updateSort } = useIncidentTableSort({
    rows: filteredRows,
  });

  const paginatedRows = useIncidentTablePagination({
    rows: sortedRows,
    page,
    rowsPerPage,
  });

  const updateDateFilter = (
    key: "dateFrom" | "dateTo",
    value: Date | null,
  ): void => {
    if (!value) {
      updateFilter(key, null);
      return;
    }

    const dateValue = new Date(value);

    if (key === "dateFrom") dateValue.setHours(0, 0, 0, 0);
    if (key === "dateTo") dateValue.setHours(23, 59, 59, 999);

    updateFilter(key, dateValue.getTime());
  };

  const renderSortableHeader = (
    key: IncidentTableSortKey,
    label: string,
  ): ReactNode => {
    return (
      <TableSortLabel
        active={sortState.key === key}
        direction={sortState.key === key ? sortState.direction : "asc"}
        onClick={() => updateSort(key)}
      >
        {label}
      </TableSortLabel>
    );
  };
  return (
    <Paper elevation={3}>
      <Box sx={{ display: "flex", flexDirection: "column", padding: "32px" }}>
        <SectionCollapsible filter open={false} label="Show Filters">
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Toolbar disableGutters sx={incidentTableStyles.filterToolbarSx}>
              <IconButton
                aria-label="Reset filters"
                size="small"
                onClick={resetFilters}
                sx={incidentTableStyles.resetFiltersButtonSx}
              >
                <DeleteForeverIcon fontSize="small" />
              </IconButton>

              <Box sx={incidentTableStyles.filterToolbarContentSx}>
                <IncidentDateFieldSwitch
                  value={filters.dateField}
                  onChange={(value) => updateFilter("dateField", value)}
                />

                <Box sx={incidentTableStyles.datePickerContainerSx}>
                  <IncidentDatePicker
                    label="From"
                    value={filters.dateFrom}
                    filterKey="dateFrom"
                    onChange={updateDateFilter}
                  />
                  <IncidentDatePicker
                    label="To"
                    value={filters.dateTo}
                    filterKey="dateTo"
                    onChange={updateDateFilter}
                  />
                </Box>

                <TextField
                  select
                  size="small"
                  label="Type"
                  value={filters.type}
                  sx={incidentTableStyles.filterTextFieldSx}
                  onChange={(event) => updateFilter("type", event.target.value)}
                >
                  <MenuItem value="" sx={incidentTableStyles.filterMenuItemSx}>
                    All
                  </MenuItem>
                  {incidentTypes.map((incidentType) => (
                    <MenuItem
                      key={incidentType}
                      value={incidentType}
                      sx={incidentTableStyles.filterMenuItemSx}
                    >
                      {incidentType}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  size="small"
                  label="Boundary"
                  value={filters.boundaryBufferKm}
                  sx={incidentTableStyles.boundaryTextFieldSx}
                  onChange={(event) =>
                    updateFilter("boundaryBufferKm", event.target.value)
                  }
                >
                  <MenuItem value="" sx={incidentTableStyles.filterMenuItemSx}>
                    All
                  </MenuItem>
                  {boundaryFilterOptions.map((boundaryFilterOption) => (
                    <MenuItem
                      key={boundaryFilterOption}
                      value={String(boundaryFilterOption)}
                      sx={incidentTableStyles.filterMenuItemSx}
                    >
                      {boundaryFilterOption}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  size="small"
                  label="Area"
                  type="number"
                  value={filters.areaM2}
                  sx={incidentTableStyles.areaTextFieldSx}
                  onChange={(event) =>
                    updateFilter("areaM2", event.target.value)
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ marginRight: 0 }}>
                        <Select
                          variant="standard"
                          disableUnderline
                          value={filters.areaOperator}
                          onChange={(event) =>
                            updateFilter(
                              "areaOperator",
                              event.target.value as IncidentAreaFilterOperator,
                            )
                          }
                          sx={incidentTableStyles.areaOperatorSelectSx}
                        >
                          <MenuItem
                            value="greaterThan"
                            sx={incidentTableStyles.filterMenuItemSx}
                          >
                            Greater than
                          </MenuItem>
                          <MenuItem
                            value="fewerThan"
                            sx={incidentTableStyles.filterMenuItemSx}
                          >
                            Fewer than
                          </MenuItem>
                          <MenuItem
                            value="equalTo"
                            sx={incidentTableStyles.filterMenuItemSx}
                          >
                            Equal to
                          </MenuItem>
                        </Select>
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">m²</InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Toolbar>
          </LocalizationProvider>
        </SectionCollapsible>

        <TableContainer
          sx={
            selectedStyle
              ? {
                  scrollbarColor: `${selectedStyle.colour} ${selectedStyle.background}`,
                  "&::-webkit-scrollbar": {
                    width: 10,
                    height: 10,
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: selectedStyle.background,
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: selectedStyle.colour,
                    borderRadius: 999,
                    border: `2px solid ${selectedStyle.background}`,
                  },
                }
              : undefined
          }
        >
          <Table
            size="small"
            aria-label="risk monitoring table"
            component="table"
          >
            <TableHead>
              <TableRow>
                <TableCellWithHover
                  align={"left"}
                  name={"Id"}
                  tooltip={
                    "The id for the set of incidents collected. Individual shape ids are contained within the shape properties"
                  }
                />
                <TableCellWithHover
                  align={"left"}
                  name={renderSortableHeader("scannedAt", "Scanned At")}
                  tooltip={"The date that the risk data was collected"}
                />

                <TableCellWithHover
                  align={"center"}
                  name={renderSortableHeader("type", "Type")}
                  tooltip={"The type of incident detected"}
                />

                <TableCellWithHover
                  align={"center"}
                  name={renderSortableHeader(
                    "incidentSpecificTime",
                    "Detection Time",
                  )}
                  tooltip={"The most accurate time available for this incident"}
                />

                <TableCellWithHover
                  align={"center"}
                  name={renderSortableHeader(
                    "boundaryBufferKm",
                    "Boundary (km)",
                  )}
                  tooltip={
                    "The buffer width that was set for this project at the time of the data being collected"
                  }
                />

                <TableCellWithHover
                  align={"right"}
                  name={renderSortableHeader("areaM2", "Potential Area (m²)")}
                  tooltip={
                    "The potential incident area represented by this row"
                  }
                />
                <TableCellWithHover
                  align={"center"}
                  name={"Download"}
                  tooltip={geojsonFileMessage}
                />
              </TableRow>
            </TableHead>

            <TableBody>
              {paginatedRows.map((row: IncidentTableData, index: number) => (
                <Row
                  key={`${row.incidentsId}-${row.scannedAt}-${index}`}
                  row={row}
                  solutionId={solutionId}
                  selectedIncident={selectedIncident}
                  selectedIncidents={selectedIncidents}
                  setSelectedIncident={setSelectedIncident}
                  setSelectedIncidents={setSelectedIncidents}
                />
              ))}
            </TableBody>

            <TableFooter>
              <TableRow>
                <TablePagination
                  count={sortedRows.length}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  rowsPerPageOptions={[10]}
                  onPageChange={(_, newPage) => setPage(newPage)}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Box>
    </Paper>
  );
}
export default ReadOnlyRiskIncidentData;
